/**
 * Case Data Normalization Utility
 * 
 * This utility provides a centralized location for normalizing case data
 * from old formats to new formats. This is especially important when
 * loading data from the API that may have been saved with older schema versions.
 * 
 * Add new normalization rules here as data formats evolve.
 */

import { CaseData, FilingStatus, YearInfoModel, MedicalCoverageType, MedicalModel, ValueUnknownReason, AssetModel, LiabilityModel, ErrorLevel, ValidationType, SpousalSupportModel, PartyModel, AlternativeIncomeModel, PensionContributionModel } from '@/types/case';
import { CountyLookup } from '@/services/lookupDataService';
import { sumBy, roundTo, getPetitionerParty, getRespondentParty } from '@/calcs/utils';
import { debugLog } from './debug';
import { ensureFinancialLineId } from './financialLineId';

function normalizeAlternativeIncomeList(others: AlternativeIncomeModel[]): AlternativeIncomeModel[] {
    return others.map((o) => {
        const legacy = (o as any)._tempId;
        const fromLegacy = typeof legacy === 'string' && legacy.trim() !== '' ? legacy.trim() : undefined;
        return {
            ...o,
            lineId: ensureFinancialLineId(o.lineId ?? fromLegacy),
        };
    });
}

/**
 * Mapping of old filing status values to new values
 * This handles cases where old data used snake_case or other formats
 * 
 * Note: 'unknown' is converted to 'single' since it's not a valid tax filing status
 * for calculations. The FilingStatus type allows 'unknown' for UI purposes,
 * but tax calculations require a valid filing status.
 */
const FILING_STATUS_MAP: Record<string, FilingStatus> = {
    'married_filing_separately': 'marriedFilingSeparately',
    'marriedFilingSeparately': 'marriedFilingSeparately', // Already correct
    'head_of_household': 'headOfHousehold',
    'headOfHousehold': 'headOfHousehold', // Already correct
    'single': 'single',
    'unknown': 'single', // Convert 'unknown' to 'single' for tax calculations
    '': null,
    'null': null,
};

/**
 * Valid filing statuses that can be used for tax calculations
 * These must match keys in taxRules.federal.filing
 */
const VALID_TAX_FILING_STATUSES = ['single', 'marriedFilingSeparately', 'headOfHousehold'] as const;

/**
 * Normalizes a filing status value from old format to new format
 * Ensures the result is a valid tax filing status (not 'unknown')
 * @param status - The filing status value to normalize
 * @returns Normalized filing status value (always a valid tax filing status or null)
 */
function normalizeFilingStatus(status: any): FilingStatus {
    if (status === null || status === undefined) {
        return null;
    }

    const statusStr = String(status).trim();
    const normalized = FILING_STATUS_MAP[statusStr] ?? 'single'; // Default to 'single' if unknown

    // Ensure we return a valid tax filing status (not 'unknown')
    // 'unknown' is allowed in the FilingStatus type for UI, but not for calculations
    if (normalized === 'unknown' || !VALID_TAX_FILING_STATUSES.includes(normalized as any)) {
        return 'single';
    }

    return normalized;
}

/**
 * Mapping of old medical coverage type values to new values
 * This handles cases where old data used snake_case or other formats
 */
const MEDICAL_COVERAGE_TYPE_MAP: Record<string, MedicalCoverageType> = {
    'title_xix': 'titleXIX',
    'titleXIX': 'titleXIX', // Already correct
    'no_cost': 'noCost',
    'noCost': 'noCost', // Already correct
    'hawk_i': 'hawkI',
    'hawkI': 'hawkI', // Already correct
    'private_pay': 'privatePay',
    'privatePay': 'privatePay', // Already correct
    'none': 'none',
    '': null,
    'null': null,
};

/**
 * Normalizes a medical coverage type value from old format to new format
 * @param coverageType - The medical coverage type value to normalize
 * @returns Normalized medical coverage type value
 */
function normalizeMedicalCoverageType(coverageType: any): MedicalCoverageType {
    if (coverageType === null || coverageType === undefined) {
        return null;
    }

    const coverageTypeStr = String(coverageType).trim();
    return MEDICAL_COVERAGE_TYPE_MAP[coverageTypeStr] ?? null; // Default to null if unknown
}

/**
 * Normalizes a MedicalModel object, converting old format values to new format
 * @param medical - The MedicalModel to normalize
 * @returns Normalized MedicalModel
 */
function normalizeMedicalModel(medical: MedicalModel | null | undefined): MedicalModel | undefined {
    if (!medical) {
        return undefined;
    }

    const normalizedCoverageType = normalizeMedicalCoverageType(medical.coverageTypeId);

    return {
        ...medical,
        // Convert null to undefined for type compatibility with MedicalModel.coverageTypeId (string | undefined)
        coverageTypeId: normalizedCoverageType === null ? undefined : normalizedCoverageType,
    };
}

/**
 * Normalizes a YearInfoModel object, converting old format values to new format
 * @param yearInfo - The YearInfoModel to normalize
 * @returns Normalized YearInfoModel (converts null to undefined for type compatibility)
 */
function normalizeYearInfo(yearInfo: YearInfoModel | null | undefined): YearInfoModel | undefined {
    if (!yearInfo) {
        return undefined;
    }

    return {
        ...yearInfo,
        status: normalizeFilingStatus(yearInfo.status),
    };
}

/**
 * Normalizes a party identifier value from old format to new format
 * @param partyValue - The party value to normalize ('party_a' or 'party_b')
 * @returns Normalized party value ('partyA' or 'partyB'), or undefined if no normalization needed
 */
function normalizePartyValue(partyValue: any): string | undefined {
    if (partyValue === null || partyValue === undefined) {
        return undefined;
    }

    const partyStr = String(partyValue).trim();
    if (partyStr === 'party_a') {
        return 'partyA';
    } else if (partyStr === 'party_b') {
        return 'partyB';
    } else if (partyStr === 'partyA' || partyStr === 'partyB') {
        return partyStr; // Already correct
    }

    // Return undefined to preserve other values (like 'shared', 'split', etc.)
    return undefined;
}

/**
 * Normalizes an affidavit type value from old format to new format
 * @param affidavitType - The affidavit type value to normalize
 * @returns Normalized affidavit type value, or undefined if no normalization needed
 */
function normalizeAffidavitType(affidavitType: any): string | undefined {
    if (affidavitType === null || affidavitType === undefined) {
        return undefined;
    }

    const typeStr = String(affidavitType).trim();
    if (typeStr === 'notary_signature_block') {
        return 'notarySignatureBlock';
    } else if (typeStr === 'penalty_of_perjury') {
        return 'penaltyOfPerjury';
    } else if (typeStr === 'notarySignatureBlock' || typeStr === 'penaltyOfPerjury') {
        return typeStr; // Already correct
    }

    // Return undefined to preserve other values
    return undefined;
}

/**
 * Normalizes a physical care value from old format to new format
 * @param physicalCare - The physical care value to normalize
 * @returns Normalized physical care value, or undefined if no normalization needed
 */
function normalizePhysicalCare(physicalCare: any): string | undefined {
    if (physicalCare === null || physicalCare === undefined) {
        return undefined;
    }

    const careStr = String(physicalCare).trim();
    if (careStr === 'no_order') {
        return 'noOrder';
    } else if (careStr === 'party_a') {
        return 'partyA';
    } else if (careStr === 'party_b') {
        return 'partyB';
    } else if (careStr === 'noOrder' || careStr === 'partyA' || careStr === 'partyB' || careStr === 'joint' || careStr === 'na') {
        return careStr; // Already correct or other valid values
    }

    // Return undefined to preserve other values
    return undefined;
}

/**
 * Normalizes an overnights per year value from old format to new format
 * @param overnightsPerYear - The overnights per year value to normalize
 * @returns Normalized overnights per year value, or undefined if no normalization needed
 */
function normalizeOvernightsPerYear(overnightsPerYear: any): string | undefined {
    if (overnightsPerYear === null || overnightsPerYear === undefined) {
        return undefined;
    }

    const valueStr = String(overnightsPerYear).trim();

    // Explicit mapping of old values to new values
    const OVERNIGHTS_MAP: Record<string, string> = {
        '0_to_127': '0To127',
        '0To127': '0To127', // Already correct
        '128_to_147': '128To147',
        '128To147': '128To147', // Already correct
        '148_to_166': '148To166',
        '148To166': '148To166', // Already correct
        '167_or_more': '167OrMore',
        '167OrMore': '167OrMore', // Already correct
    };

    // Check if it's in our mapping
    if (OVERNIGHTS_MAP[valueStr]) {
        return OVERNIGHTS_MAP[valueStr];
    }

    // Normalize 'n/a' to 'na' for consistency with other fields
    const normalized = valueStr.toLowerCase();
    if (normalized === 'n/a' || normalized === 'na') {
        return 'na';
    }

    // Preserve other valid values (like 'unknown', etc.)
    if (valueStr === 'unknown') {
        return valueStr;
    }

    // Return undefined to preserve other values
    return undefined;
}

/**
 * Determines the allocation type based on valueUnknownReason
 * @param valueUnknownReason - The value unknown reason
 * @returns Allocation type: 'notVerified', 'termOrPension', or 'monetary'
 */
function getAllocationTypeFromValueUnknownReason(valueUnknownReason: ValueUnknownReason): 'monetary' | 'notVerified' | 'termOrPension' | null {
    if (valueUnknownReason === 'notVerified') {
        return 'notVerified';
    } else if (valueUnknownReason === 'termOrPension') {
        return 'termOrPension';
    }
    return 'monetary';
}

/**
 * Normalizes a value unknown reason from old format to new format
 * @param valueUnknownReason - The value unknown reason to normalize
 * @returns Normalized value unknown reason, or undefined if no normalization needed
 */
function normalizeValueUnknownReason(valueUnknownReason: any): ValueUnknownReason {
    if (valueUnknownReason === null || valueUnknownReason === undefined) {
        return null;
    }

    const reasonStr = String(valueUnknownReason).trim();

    // Explicit mapping of old values to new values
    const VALUE_UNKNOWN_REASON_MAP: Record<string, ValueUnknownReason> = {
        'not_verified': 'notVerified',
        'notVerified': 'notVerified', // Already correct
        'term_or_pension': 'termOrPension',
        'termOrPension': 'termOrPension', // Already correct
    };

    // Check if it's in our mapping
    if (VALUE_UNKNOWN_REASON_MAP[reasonStr]) {
        return VALUE_UNKNOWN_REASON_MAP[reasonStr];
    }

    // Return null for invalid values
    return null;
}

/**
 * Normalizes a spousal support other party value from old format to new format
 * @param otherParty - The other party value to normalize
 * @returns Normalized other party value, or undefined if no normalization needed
 */
function normalizeSpousalSupportOtherParty(otherParty: any): string | undefined {
    if (otherParty === null || otherParty === undefined) {
        return undefined;
    }

    const partyStr = String(otherParty).trim();
    if (partyStr === 'third_party') {
        return 'thirdParty';
    } else if (partyStr === 'party' || partyStr === 'thirdParty') {
        return partyStr; // Already correct
    }

    // Return undefined to preserve other values
    return undefined;
}

/** Net Worth / allocation asset buckets — every asset row must land in exactly one. */
export const ASSET_SUBCATEGORY_BUCKETS = [
    'realEstate',
    'vehicles',
    'lifeInsurance',
    'securities',
    'pensions',
    'cash',
    'personalProperty',
    'other',
] as const;
export type AssetSubcategoryBucket = (typeof ASSET_SUBCATEGORY_BUCKETS)[number];

const ASSET_SUBCATEGORY_BUCKET_SET = new Set<string>(ASSET_SUBCATEGORY_BUCKETS);

/** Collapse user/legacy labels to snake_case-ish keys for SUBCATEGORY_MAP lookup. */
function assetSubcategoryLookupKeys(raw: string): string[] {
    const trimmed = raw.trim();
    const lower = trimmed.toLowerCase();
    const slug = lower
        .replace(/&/g, 'and')
        .replace(/[-–—/\\.,;:()+]+/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    const compact = lower.replace(/[^a-z0-9]+/g, '');
    const ordered = [trimmed, lower, slug];
    if (compact && compact !== lower.replace(/\s+/g, '')) {
        ordered.push(compact);
    }
    return [...new Set(ordered.filter((k) => k.length > 0))];
}

/**
 * Normalizes an asset subcategory value from old format to new format
 * @param subcategory - The subcategory value to normalize
 * @returns Normalized subcategory value, or undefined if no normalization needed
 */
function normalizeAssetSubcategory(subcategory: any): string | undefined {
    if (subcategory === null || subcategory === undefined) {
        return undefined;
    }

    const subcategoryStr = String(subcategory).trim();
    if (subcategoryStr === '') {
        return undefined;
    }

    // Explicit mapping: legacy snake_case, imports, spreadsheets, OCR, common typos.
    // Keys are matched after trim; also lowercased / slugified via assetSubcategoryLookupKeys.
    const SUBCATEGORY_MAP: Record<string, string> = {
        // --- realEstate ---
        'real_estate': 'realEstate',
        'realestate': 'realEstate',
        'realEstate': 'realEstate',
        'real_property': 'realEstate',
        'realproperty': 'realEstate',
        'primary_residence': 'realEstate',
        'primaryresidence': 'realEstate',
        'marital_home': 'realEstate',
        'maritalhome': 'realEstate',
        'matrimonial_home': 'realEstate',
        'home': 'realEstate',
        'homes': 'realEstate',
        'house': 'realEstate',
        'housing': 'realEstate',
        'land': 'realEstate',
        'lot': 'realEstate',
        'lots': 'realEstate',
        'acreage': 'realEstate',
        'farm': 'realEstate',
        'farmland': 'realEstate',
        'rental_property': 'realEstate',
        'rentalproperty': 'realEstate',
        'rental': 'realEstate',
        'investment_property': 'realEstate',
        'investmentproperty': 'realEstate',
        'investment_properties': 'realEstate',
        'commercial_property': 'realEstate',
        'commercialproperty': 'realEstate',
        'condo': 'realEstate',
        'condominium': 'realEstate',
        'townhome': 'realEstate',
        'townhouse': 'realEstate',
        'mobile_home': 'realEstate',
        'mobilehome': 'realEstate',
        'timeshare': 'realEstate',
        'time_share': 'realEstate',

        // --- vehicles ---
        'vehicles': 'vehicles',
        'vehicle': 'vehicles',
        'vehical': 'vehicles',
        'vehicals': 'vehicles',
        'vehciles': 'vehicles',
        'autos': 'vehicles',
        'auto': 'vehicles',
        'automobile': 'vehicles',
        'automobiles': 'vehicles',
        'car': 'vehicles',
        'cars': 'vehicles',
        'truck': 'vehicles',
        'trucks': 'vehicles',
        'suv': 'vehicles',
        'motor_vehicle': 'vehicles',
        'motorvehicle': 'vehicles',
        'motor_vehicles': 'vehicles',
        'recreational_vehicle': 'vehicles',
        'rv': 'vehicles',
        'boat': 'vehicles',
        'boats': 'vehicles',
        'watercraft': 'vehicles',
        'atv': 'vehicles',
        'atvs': 'vehicles',
        'motorcycle': 'vehicles',
        'motorcycles': 'vehicles',
        'snowmobile': 'vehicles',
        'snowmobiles': 'vehicles',
        'farm_equipment': 'vehicles',
        'farm_equip': 'vehicles',
        'tractor': 'vehicles',
        'trailor': 'vehicles',
        'trailer': 'vehicles',
        'trailers': 'vehicles',

        // --- lifeInsurance ---
        'life_insurance': 'lifeInsurance',
        'lifeInsurance': 'lifeInsurance',
        'lifeinsurance': 'lifeInsurance',
        'life_ins': 'lifeInsurance',
        'lifeins': 'lifeInsurance',
        'term_life': 'lifeInsurance',
        'termlife': 'lifeInsurance',
        'whole_life': 'lifeInsurance',
        'wholelife': 'lifeInsurance',
        'universal_life': 'lifeInsurance',
        'universallife': 'lifeInsurance',
        'insurance_life': 'lifeInsurance',

        // --- securities ---
        'securities': 'securities',
        'security': 'securities',
        'stocks': 'securities',
        'stock': 'securities',
        'equities': 'securities',
        'equity': 'securities',
        'bonds': 'securities',
        'bond': 'securities',
        'mutual_funds': 'securities',
        'mutualfunds': 'securities',
        'mutual_fund': 'securities',
        'etf': 'securities',
        'etfs': 'securities',
        'brokerage': 'securities',
        'brokerage_account': 'securities',
        'brokerageaccount': 'securities',
        'investment_account': 'securities',
        'investmentaccount': 'securities',
        'investments': 'securities',
        'portfolio': 'securities',
        'crypto': 'securities',
        'cryptocurrency': 'securities',
        'bitcoin': 'securities',
        'stock_options': 'securities',
        'stockoptions': 'securities',
        'options': 'securities',
        'rsus': 'securities',
        'rsu': 'securities',
        'espp': 'securities',
        'money_market_fund': 'securities',
        'moneymarketfund': 'securities',

        // --- pensions ---
        'pensions': 'pensions',
        'pension': 'pensions',
        'retirement': 'pensions',
        'retirement_account': 'pensions',
        'retirementaccount': 'pensions',
        '401k': 'pensions',
        '401_k': 'pensions',
        '403b': 'pensions',
        '403_b': 'pensions',
        '457': 'pensions',
        '457b': 'pensions',
        'ira': 'pensions',
        'roth_ira': 'pensions',
        'rothira': 'pensions',
        'traditional_ira': 'pensions',
        'traditionalira': 'pensions',
        'sep_ira': 'pensions',
        'sepira': 'pensions',
        'simple_ira': 'pensions',
        'simpleira': 'pensions',
        'annuity': 'pensions',
        'annuities': 'pensions',
        'defined_benefit': 'pensions',
        'definedbenefit': 'pensions',
        'defined_contribution': 'pensions',
        'definedcontribution': 'pensions',
        'profit_sharing': 'pensions',
        'profitsharing': 'pensions',
        'deferred_comp': 'pensions',
        'deferredcomp': 'pensions',
        '457_plan': 'pensions',

        // --- cash (bank / liquid) ---
        'cash': 'cash',
        'cash_and_bank_accounts': 'cash',
        'cash_and_bank': 'cash',
        'cash_bank_accounts': 'cash',
        'cash_bank': 'cash',
        'bank_accounts': 'cash',
        'bankaccounts': 'cash',
        'bank_accounts_and_cash': 'cash',
        'bank_account': 'cash',
        'banking': 'cash',
        'banks': 'cash',
        'bank': 'cash',
        'checking': 'cash',
        'checking_account': 'cash',
        'checkingaccount': 'cash',
        'savings': 'cash',
        'savings_account': 'cash',
        'savingsaccount': 'cash',
        'money_market': 'cash',
        'moneymarket': 'cash',
        'mm_account': 'cash',
        'cd': 'cash',
        'cds': 'cash',
        'certificate_of_deposit': 'cash',
        'demand_deposit': 'cash',
        'dda': 'cash',
        'liquid': 'cash',
        'liquidity': 'cash',
        'paypal': 'cash',
        'venmo': 'cash',
        'cash_on_hand': 'cash',
        'cashonhand': 'cash',
        'petty_cash': 'cash',
        'pettycash': 'cash',
        'bank accounts': 'cash',
        'bank account': 'cash',
        'bank accts': 'cash',
        'bank acct': 'cash',
        'bank_acct': 'cash',
        'bank_accts': 'cash',
        'cash and bank accounts': 'cash',
        'cash & bank accounts': 'cash',
        'cash_and_checks': 'cash',
        'depository': 'cash',
        'credit_union': 'cash',
        'creditunion': 'cash',

        // --- personalProperty ---
        'personal_property': 'personalProperty',
        'personalProperty': 'personalProperty',
        'personalproperty': 'personalProperty',
        'personal': 'personalProperty',
        'household': 'personalProperty',
        'household_goods': 'personalProperty',
        'householdgoods': 'personalProperty',
        'furniture': 'personalProperty',
        'furnishings': 'personalProperty',
        'jewelry': 'personalProperty',
        'jewellery': 'personalProperty',
        'collectibles': 'personalProperty',
        'collectible': 'personalProperty',
        'art': 'personalProperty',
        'firearms': 'personalProperty',
        'guns': 'personalProperty',
        'tools': 'personalProperty',
        'electronics': 'personalProperty',
        'computer': 'personalProperty',
        'computers': 'personalProperty',
        'tangible_personal_property': 'personalProperty',
        'tpp': 'personalProperty',

        // --- other ---
        'other': 'other',
        'misc': 'other',
        'miscellaneous': 'other',
        'miscellanous': 'other',
        'general': 'other',
        'unknown': 'other',
        'unclassified': 'other',
        'various': 'other',
    };

    const resolve = (key: string): string | undefined => SUBCATEGORY_MAP[key];

    for (const key of assetSubcategoryLookupKeys(subcategoryStr)) {
        const v = resolve(key);
        if (v) {
            return v;
        }
    }
    return undefined;
}

/**
 * Maps any legacy or inconsistent subcategory string to a single Net Worth bucket.
 * Unknown values become `other` so rows are never dropped when splitting assets by section.
 */
export function canonicalAssetSubcategory(subcategory: unknown): AssetSubcategoryBucket {
    const mapped = normalizeAssetSubcategory(subcategory);
    if (mapped && ASSET_SUBCATEGORY_BUCKET_SET.has(mapped)) {
        return mapped as AssetSubcategoryBucket;
    }
    return 'other';
}

/**
 * Normalizes an error level value from old format to new format
 * @param errorLevel - The error level value to normalize
 * @returns Normalized error level value, or undefined if no normalization needed
 */
function normalizeErrorLevel(errorLevel: any): ErrorLevel {
    if (errorLevel === null || errorLevel === undefined) {
        return null;
    }

    const levelStr = String(errorLevel).trim();

    // Explicit mapping of old values to new values
    const ERROR_LEVEL_MAP: Record<string, ErrorLevel> = {
        'validation_error': 'validationError',
        'validationError': 'validationError', // Already correct
        'calculation_error': 'calculationError',
        'calculationError': 'calculationError', // Already correct
        'skipped': 'skipped',
    };

    // Check if it's in our mapping
    if (ERROR_LEVEL_MAP[levelStr]) {
        return ERROR_LEVEL_MAP[levelStr];
    }

    // Return null for invalid values
    return null;
}

/**
 * Normalizes a validation type value from old format to new format
 * @param validationType - The validation type value to normalize
 * @returns Normalized validation type value, or undefined if no normalization needed
 */
function normalizeValidationType(validationType: any): ValidationType {
    if (validationType === null || validationType === undefined) {
        return null;
    }

    const typeStr = String(validationType).trim();

    // Explicit mapping of old values to new values
    const VALIDATION_TYPE_MAP: Record<string, ValidationType> = {
        'not_started': 'notStarted',
        'notStarted': 'notStarted', // Already correct
        'warning': 'warning',
        'success': 'success',
    };

    // Check if it's in our mapping
    if (VALIDATION_TYPE_MAP[typeStr]) {
        return VALIDATION_TYPE_MAP[typeStr];
    }

    // Return null for invalid values
    return null;
}

/**
 * Normalizes a team status value from old format to new format
 * @param teamStatus - The team status value to normalize
 * @returns Normalized team status value, or undefined if no normalization needed
 */
function normalizeTeamStatus(teamStatus: any): string | undefined {
    if (teamStatus === null || teamStatus === undefined) {
        return undefined;
    }

    const statusStr = String(teamStatus).trim();

    // Explicit mapping of old values to new values
    const TEAM_STATUS_MAP: Record<string, string> = {
        'pending_payment': 'pendingPayment',
        'pendingPayment': 'pendingPayment', // Already correct
        'active': 'active',
        'inactive': 'inactive',
    };

    // Check if it's in our mapping
    if (TEAM_STATUS_MAP[statusStr]) {
        return TEAM_STATUS_MAP[statusStr];
    }

    // Return undefined to preserve other values
    return undefined;
}

/**
 * Normalizes a team billing type value from old format to new format
 * @param billingType - The billing type value to normalize
 * @returns Normalized billing type value, or undefined if no normalization needed
 */
function normalizeBillingType(billingType: any): string | undefined {
    if (billingType === null || billingType === undefined) {
        return undefined;
    }

    const typeStr = String(billingType).trim();

    // Explicit mapping of old values to new values
    const BILLING_TYPE_MAP: Record<string, string> = {
        'manual_subscription_cost': 'manualSubscriptionCost',
        'manualSubscriptionCost': 'manualSubscriptionCost', // Already correct
        'normal': 'normal',
        'free': 'free',
    };

    // Check if it's in our mapping
    if (BILLING_TYPE_MAP[typeStr]) {
        return BILLING_TYPE_MAP[typeStr];
    }

    // Return undefined to preserve other values
    return undefined;
}

/**
 * Populates countyName from countyId if countyName is missing
 * @param basic - The BasicModel to normalize
 * @param counties - Optional array of counties lookup data
 */
export function populateCountyName(basic: CaseData['basic'], counties?: CountyLookup[]): void {
    if (!basic || !basic.countyId) {
        return; // No countyId
    }

    // Only populate countyName if it's truly missing (null/undefined) or empty string
    // Don't overwrite if countyName already exists - this prevents normalization from overwriting user-selected values
    // This function should ONLY be called during API normalization, not during form updates
    if (basic.countyName === null || basic.countyName === undefined || basic.countyName === '') {
        if (counties && counties.length > 0) {
            const county = counties.find(c => c.id === basic.countyId);
            if (county) {
                basic.countyName = county.name;
            }
        }
    }
}

/**
 * Calculates applicableRateText for Social Security and Medicare Tax based on party income data
 * @param party - The party model to calculate applicableRateText for
 * @returns The applicable rate text ('7.65%', '15.3%', or '7.65% / 15.3%')
 */
function calculateApplicableRateText(party: PartyModel | null | undefined): string {
    if (!party) {
        return '7.65%'; // Default value
    }

    const wages = party?.income?.primary?.yearlyWages ?? 0.0;
    const selfEmployment = party?.income?.primary?.yearlySelfEmployment ?? 0.0;
    const otherIncomes = sumBy<AlternativeIncomeModel>(party?.income?.others ?? [], x => x?.isFica === true ? (x?.amount ?? 0.0) : 0.0);
    const pensionDeductionsNoWages = sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === false && x?.isFicaApplicable === true ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0);
    const pensionDeductionsWithWages = sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === true && x?.isFicaApplicable === false ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0);

    const childCareReimbursements = 0.0;
    const ficaWages = roundTo(wages + otherIncomes + pensionDeductionsNoWages + childCareReimbursements - Math.abs(pensionDeductionsWithWages), 2);

    // Calculate applicableRateText based on selfEmployment and ficaWages
    if ((selfEmployment ?? 0.0) === 0.0) {
        return '7.65%';
    } else if ((ficaWages ?? 0.0) === 0.0) {
        return '15.3%';
    } else {
        return '7.65% / 15.3%';
    }
}

/** True if `existing` is missing/blank — i.e. `ensureFinancialLineId(existing)` would mint a brand new random id rather than reuse one. Exported so pages (BudgetPage, NetWorthPage) can reuse a matching local row's id by name/identity before falling back to a fresh mint, instead of re-minting a random id on every incoming pass (see docs/AUTOSAVE_DETAILS.md section 13). */
export function isMissingLineId(existing: string | null | undefined): boolean {
    return typeof existing !== 'string' || existing.trim() === '';
}

/**
 * Pre-scan (read-only, no mutation) for whether `normalizeCaseData` would inject at least one
 * brand-new `lineId`/`groupLineId` into this case — i.e. the case (typically a legacy one saved
 * before stable row identity existed) has financial rows missing an id.
 *
 * Used by the load path to decide whether to persist the newly-injected ids immediately (see
 * `docs/AUTOSAVE_DETAILS.md` section 13 "lineId availability & legacy backfill"). Without a
 * persist-on-load, `ensureFinancialLineId` mints a *different* random id on every fresh load
 * until the case happens to be saved, so a legacy case's row identity would be unstable across
 * loads/tabs until the user's first edit — and, for collections mirrored into a page's local
 * state with a `reconcileKeyedArray` hydrate effect (BudgetPage's expense groups/expenses,
 * NetWorthPage's assets/liabilities), that instability can duplicate the whole row (see
 * `useKeyedArrayMirror.ts` doc comment).
 *
 * Covered collections (every id-bearing row currently reachable by a wizard page):
 * `financial.assets[].lineId`, `financial.liabilities[].lineId`,
 * `financial.expenseGroups[].groupLineId` + `.expenses[].lineId`,
 * `parties.{partyA,partyB}.income.others[].lineId`, `parties.{partyA,partyB}.spousalSupports[].lineId`,
 * `parties.{partyA,partyB}.pensions[].lineId`. Mirrors the exact set `normalizeCaseData` backfills
 * below — keep in sync (a Vitest suite asserts this invariant; see
 * `web/src/utils/caseDataNormalization.hasAnyMissingFinancialLineId.test.ts`).
 *
 * Deliberately NOT covered:
 * - `documents.stepDownCases[]` / `documents.splitCases[]` / `documents.alternatingEvenCase` —
 *   each a full nested `CaseData`, but these are *derived*: `recalculate()` regenerates them on
 *   every pass, so any id backfilled here would be discarded on the next recalc. No wizard page
 *   reads them into a mirrored/reconciled local array (they're read-only display), so they don't
 *   participate in the duplication failure mode this scan exists to prevent. If a nested case's
 *   rows ever need stable ids, fix it in the calc layer (copy the primary case's ids into the
 *   derived copy), not here.
 * - `tax.stepDowns` — has no id field. The page that edits it binds directly to context (not
 *   mirrored into local `useState` with a reconcile hydrate effect), so it's never passed through
 *   `reconcileKeyedArray` and needs no stable id for this bug. Add one only if that ever changes.
 */
export function hasAnyMissingFinancialLineId(caseData: CaseData): boolean {
    const assets = caseData.financial?.assets ?? [];
    if (assets.some((a) => isMissingLineId(a.lineId))) return true;

    const liabilities = caseData.financial?.liabilities ?? [];
    if (liabilities.some((l) => isMissingLineId(l.lineId))) return true;

    const expenseGroups = caseData.financial?.expenseGroups ?? [];
    for (const group of expenseGroups) {
        if (isMissingLineId(group.groupLineId)) return true;
        if ((group.expenses ?? []).some((e) => isMissingLineId(e.lineId))) return true;
    }

    for (const party of [caseData.parties?.partyA, caseData.parties?.partyB]) {
        if (!party) continue;
        const others = party.income?.others ?? [];
        if (others.some((o) => isMissingLineId(o.lineId ?? (o as any)._tempId))) return true;
        if ((party.spousalSupports ?? []).some((s) => isMissingLineId(s.lineId))) return true;
        if ((party.pensions ?? []).some((p) => isMissingLineId(p.lineId))) return true;
    }

    return false;
}

/**
 * Recursively normalizes all YearInfoModel objects in a CaseData structure
 * @param caseData - The case data to normalize
 * @param counties - Optional array of counties lookup data for populating countyName
 * @returns Normalized case data
 */
export function normalizeCaseData(caseData: CaseData, counties?: CountyLookup[]): CaseData {

    debugLog("Normalizing case data...");
    if (!caseData) {
        debugLog("No case data to normalize!");
        return caseData;
    }

    const normalized: CaseData = { ...caseData };

    // Normalize representation.petitionerParty and representation.representedParty
    if (normalized.representation) {
        normalized.representation = {
            ...normalized.representation,
            petitionerParty: normalizePartyValue(normalized.representation.petitionerParty) ?? normalized.representation.petitionerParty,
            representedParty: normalizePartyValue(normalized.representation.representedParty) ?? normalized.representation.representedParty,
        };
    }

    // Normalize children.custodyArrangement, children.physicalCare, and children.overnightsPerYear
    if (normalized.children) {
        normalized.children = {
            ...normalized.children,
            custodyArrangement: normalizePartyValue(normalized.children.custodyArrangement) ?? normalized.children.custodyArrangement,
            physicalCare: normalizePhysicalCare(normalized.children.physicalCare) ?? normalized.children.physicalCare,
            overnightsPerYear: normalizeOvernightsPerYear(normalized.children.overnightsPerYear) ?? normalized.children.overnightsPerYear,
        };

        // Normalize splits array if it exists
        if (normalized.children.splits) {
            normalized.children = {
                ...normalized.children,
                splits: normalized.children.splits.map(split => ({
                    ...split,
                    custodyArrangement: normalizePartyValue(split.custodyArrangement) ?? split.custodyArrangement,
                    overnightsPerYear: normalizeOvernightsPerYear(split.overnightsPerYear) ?? split.overnightsPerYear,
                })),
            };
        }
    }

    // Normalize basic.affidavit.type
    if (normalized.basic?.affidavit) {
        normalized.basic = {
            ...normalized.basic,
            affidavit: {
                ...normalized.basic.affidavit,
                type: normalizeAffidavitType(normalized.basic.affidavit.type) ?? normalized.basic.affidavit.type,
            },
        };
    }

    // Populate countyName from countyId if missing
    if (normalized.basic) {
        populateCountyName(normalized.basic, counties);
    }

    // Normalize parties
    if (normalized.parties) {
        // Normalize partyA
        if (normalized.parties.partyA) {
            normalized.parties.partyA = {
                ...normalized.parties.partyA,
                medical: normalizeMedicalModel(normalized.parties.partyA.medical),
            };

            if (normalized.parties.partyA.tax?.claimSchedule) {
                normalized.parties.partyA = {
                    ...normalized.parties.partyA,
                    tax: {
                        ...normalized.parties.partyA.tax,
                        claimSchedule: {
                            ...normalized.parties.partyA.tax.claimSchedule,
                            all: normalizeYearInfo(normalized.parties.partyA.tax.claimSchedule.all),
                            odd: normalizeYearInfo(normalized.parties.partyA.tax.claimSchedule.odd),
                            even: normalizeYearInfo(normalized.parties.partyA.tax.claimSchedule.even),
                        },
                    },
                };
            }
        }

        // Normalize partyB
        if (normalized.parties.partyB) {
            normalized.parties.partyB = {
                ...normalized.parties.partyB,
                medical: normalizeMedicalModel(normalized.parties.partyB.medical),
            };

            if (normalized.parties.partyB.tax?.claimSchedule) {
                normalized.parties.partyB = {
                    ...normalized.parties.partyB,
                    tax: {
                        ...normalized.parties.partyB.tax,
                        claimSchedule: {
                            ...normalized.parties.partyB.tax.claimSchedule,
                            all: normalizeYearInfo(normalized.parties.partyB.tax.claimSchedule.all),
                            odd: normalizeYearInfo(normalized.parties.partyB.tax.claimSchedule.odd),
                            even: normalizeYearInfo(normalized.parties.partyB.tax.claimSchedule.even),
                        },
                    },
                };
            }
        }

        if (normalized.parties.partyA?.income?.others) {
            normalized.parties.partyA = {
                ...normalized.parties.partyA,
                income: {
                    ...normalized.parties.partyA.income!,
                    others: normalizeAlternativeIncomeList(normalized.parties.partyA.income!.others!),
                },
            };
        }
        if (normalized.parties.partyB?.income?.others) {
            normalized.parties.partyB = {
                ...normalized.parties.partyB,
                income: {
                    ...normalized.parties.partyB.income!,
                    others: normalizeAlternativeIncomeList(normalized.parties.partyB.income!.others!),
                },
            };
        }

        // Normalize partyA spousal supports
        if (normalized.parties.partyA?.spousalSupports) {
            normalized.parties.partyA = {
                ...normalized.parties.partyA,
                spousalSupports: normalized.parties.partyA.spousalSupports.map(support => ({
                    ...support,
                    lineId: ensureFinancialLineId(support.lineId),
                    otherParty: normalizeSpousalSupportOtherParty(support.otherParty) ?? support.otherParty,
                    // Normalize originalOtherParty if it exists (debug field)
                    originalOtherParty: (support as any).originalOtherParty
                        ? normalizeSpousalSupportOtherParty((support as any).originalOtherParty) ?? (support as any).originalOtherParty
                        : (support as any).originalOtherParty,
                })),
            };
        }

        // Normalize partyB spousal supports
        if (normalized.parties.partyB?.spousalSupports) {
            normalized.parties.partyB = {
                ...normalized.parties.partyB,
                spousalSupports: normalized.parties.partyB.spousalSupports.map(support => ({
                    ...support,
                    lineId: ensureFinancialLineId(support.lineId),
                    otherParty: normalizeSpousalSupportOtherParty(support.otherParty) ?? support.otherParty,
                    // Normalize originalOtherParty if it exists (debug field)
                    originalOtherParty: (support as any).originalOtherParty
                        ? normalizeSpousalSupportOtherParty((support as any).originalOtherParty) ?? (support as any).originalOtherParty
                        : (support as any).originalOtherParty,
                })),
            };
        }

        if (normalized.parties.partyA?.pensions?.length) {
            normalized.parties.partyA = {
                ...normalized.parties.partyA,
                pensions: normalized.parties.partyA.pensions.map((p) => ({
                    ...p,
                    lineId: ensureFinancialLineId(p.lineId),
                })),
            };
        }
        if (normalized.parties.partyB?.pensions?.length) {
            normalized.parties.partyB = {
                ...normalized.parties.partyB,
                pensions: normalized.parties.partyB.pensions.map((p) => ({
                    ...p,
                    lineId: ensureFinancialLineId(p.lineId),
                })),
            };
        }
    }

    // Normalize financial.assets and financial.liabilities
    if (normalized.financial) {
        // Normalize assets
        if (normalized.financial.assets) {
            normalized.financial = {
                ...normalized.financial,
                assets: normalized.financial.assets.map(asset => {
                    const normalizedValueUnknownReason = normalizeValueUnknownReason(asset.valueUnknownReason);
                    const allocationType = getAllocationTypeFromValueUnknownReason(normalizedValueUnknownReason);

                    // Normalize assetAllocation types
                    let normalizedAssetAllocation = asset.assetAllocation;
                    if (normalizedAssetAllocation) {
                        normalizedAssetAllocation = {
                            ...normalizedAssetAllocation,
                            partyA: normalizedAssetAllocation.partyA ? {
                                ...normalizedAssetAllocation.partyA,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedAssetAllocation.partyA.type !== undefined
                                    ? normalizedAssetAllocation.partyA.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedAssetAllocation.partyA.amount !== undefined && normalizedAssetAllocation.partyA.amount !== null && normalizedAssetAllocation.partyA.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedAssetAllocation.partyA,
                            partyB: normalizedAssetAllocation.partyB ? {
                                ...normalizedAssetAllocation.partyB,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedAssetAllocation.partyB.type !== undefined
                                    ? normalizedAssetAllocation.partyB.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedAssetAllocation.partyB.amount !== undefined && normalizedAssetAllocation.partyB.amount !== null && normalizedAssetAllocation.partyB.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedAssetAllocation.partyB,
                        };
                    }

                    // Normalize debtAllocation types (for assets)
                    let normalizedDebtAllocation = asset.debtAllocation;
                    if (normalizedDebtAllocation) {
                        normalizedDebtAllocation = {
                            ...normalizedDebtAllocation,
                            partyA: normalizedDebtAllocation.partyA ? {
                                ...normalizedDebtAllocation.partyA,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedDebtAllocation.partyA.type !== undefined
                                    ? normalizedDebtAllocation.partyA.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedDebtAllocation.partyA.amount !== undefined && normalizedDebtAllocation.partyA.amount !== null && normalizedDebtAllocation.partyA.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedDebtAllocation.partyA,
                            partyB: normalizedDebtAllocation.partyB ? {
                                ...normalizedDebtAllocation.partyB,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedDebtAllocation.partyB.type !== undefined
                                    ? normalizedDebtAllocation.partyB.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedDebtAllocation.partyB.amount !== undefined && normalizedDebtAllocation.partyB.amount !== null && normalizedDebtAllocation.partyB.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedDebtAllocation.partyB,
                        };
                    }

                    return {
                        ...asset,
                        lineId: ensureFinancialLineId(asset.lineId),
                        valueUnknownReason: normalizedValueUnknownReason,
                        subcategory: canonicalAssetSubcategory(asset.subcategory),
                        owner: normalizePartyValue(asset.owner) ?? asset.owner,
                        assetAllocation: normalizedAssetAllocation,
                        debtAllocation: normalizedDebtAllocation,
                    };
                }),
            };
        }

        // Normalize liabilities
        if (normalized.financial.liabilities) {
            normalized.financial = {
                ...normalized.financial,
                liabilities: normalized.financial.liabilities.map(liability => {
                    const normalizedValueUnknownReason = normalizeValueUnknownReason(liability.valueUnknownReason);
                    const allocationType = getAllocationTypeFromValueUnknownReason(normalizedValueUnknownReason);

                    // Normalize debtAllocation types
                    let normalizedDebtAllocation = liability.debtAllocation;
                    if (normalizedDebtAllocation) {
                        normalizedDebtAllocation = {
                            ...normalizedDebtAllocation,
                            partyA: normalizedDebtAllocation.partyA ? {
                                ...normalizedDebtAllocation.partyA,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedDebtAllocation.partyA.type !== undefined
                                    ? normalizedDebtAllocation.partyA.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedDebtAllocation.partyA.amount !== undefined && normalizedDebtAllocation.partyA.amount !== null && normalizedDebtAllocation.partyA.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedDebtAllocation.partyA,
                            partyB: normalizedDebtAllocation.partyB ? {
                                ...normalizedDebtAllocation.partyB,
                                // CRITICAL: Preserve explicitly set types (including null, explicitlyUnassigned, notVerified, termOrPension, monetary)
                                // If type is undefined, set it based on valueUnknownReason if it's notVerified/termOrPension,
                                // otherwise only set if amount is non-zero
                                type: normalizedDebtAllocation.partyB.type !== undefined
                                    ? normalizedDebtAllocation.partyB.type
                                    : (allocationType === 'notVerified' || allocationType === 'termOrPension')
                                        ? allocationType  // Always set type for notVerified/termOrPension, even if amount is 0
                                        : (normalizedDebtAllocation.partyB.amount !== undefined && normalizedDebtAllocation.partyB.amount !== null && normalizedDebtAllocation.partyB.amount !== 0)
                                            ? allocationType
                                            : 'explicitlyUnassigned',
                            } : normalizedDebtAllocation.partyB,
                        };
                    }

                    return {
                        ...liability,
                        lineId: ensureFinancialLineId(liability.lineId),
                        valueUnknownReason: normalizedValueUnknownReason,
                        owner: normalizePartyValue(liability.owner) ?? liability.owner,
                        debtAllocation: normalizedDebtAllocation,
                    };
                }),
            };
        }

        // Ensure each budget expense row has a stable lineId
        if (normalized.financial.expenseGroups) {
            normalized.financial = {
                ...normalized.financial,
                expenseGroups: normalized.financial.expenseGroups.map((group) => ({
                    ...group,
                    groupLineId: ensureFinancialLineId(group.groupLineId),
                    expenses: (group.expenses || []).map((expense) => ({
                        ...expense,
                        lineId: ensureFinancialLineId(expense.lineId),
                    })),
                })),
            };
        }
    }

    // Normalize metadata.errorLevel
    if (normalized.metadata) {
        normalized.metadata = {
            ...normalized.metadata,
            errorLevel: normalizeErrorLevel(normalized.metadata.errorLevel) ?? normalized.metadata.errorLevel,
        };
    }

    // Normalize validation levels
    if (normalized.validation) {
        const normalizedValidation: typeof normalized.validation = { ...normalized.validation };

        // Normalize all validation level objects
        const validationKeys: (keyof typeof normalized.validation)[] = [
            'caseInfo', 'custody', 'income', 'taxes', 'deductions', 'medical',
            'stepDowns', 'networth', 'budget', 'division', 'childSupportDocs', 'financialDocs'
        ];

        for (const key of validationKeys) {
            if (normalizedValidation[key]) {
                normalizedValidation[key] = {
                    ...normalizedValidation[key],
                    level: normalizeValidationType(normalizedValidation[key]?.level) ?? normalizedValidation[key]?.level,
                };
            }
        }

        normalized.validation = normalizedValidation;
    }

    // Normalize team.status and team.billingType
    if (normalized.team) {
        normalized.team = {
            ...normalized.team,
            status: normalizeTeamStatus(normalized.team.status) ?? normalized.team.status,
            billingType: normalizeBillingType(normalized.team.billingType) ?? normalized.team.billingType,
        };
    }

    // Normalize documents (calculation results may contain snake_case values)
    if (normalized.documents) {
        // Ensure documents is a copy, not a reference
        normalized.documents = { ...normalized.documents };

        // Normalize highIncomeEstimator so camelCase keys are always present (API may send PascalCase)
        const he = normalized.documents.highIncomeEstimator as Record<string, unknown> | undefined;
        if (he && typeof he === 'object') {
            normalized.documents = {
                ...normalized.documents,
                highIncomeEstimator: {
                    extrapolationMethod: he.extrapolationMethod ?? (he as any).ExtrapolationMethod ?? undefined,
                    extrapolatedPercent: he.extrapolatedPercent ?? (he as any).ExtrapolatedPercent ?? undefined,
                    estimatedMonthlyObligation: he.estimatedMonthlyObligation ?? (he as any).EstimatedMonthlyObligation ?? undefined,
                    combinedIncome: he.combinedIncome ?? (he as any).CombinedIncome ?? undefined,
                    payorPercentageOfCombined: he.payorPercentageOfCombined ?? (he as any).PayorPercentageOfCombined ?? undefined,
                    netOffsetPayor: he.netOffsetPayor ?? (he as any).NetOffsetPayor ?? undefined,
                    sharedEstimate: he.sharedEstimate ?? (he as any).SharedEstimate ?? undefined,
                    primaryPhysicalEstimate: he.primaryPhysicalEstimate ?? (he as any).PrimaryPhysicalEstimate ?? undefined,
                },
            };
        }

        // Normalize taxInfoUsed.taxStatus in worksheet1 debug sections
        // This can appear in various debug sections, so we normalize it recursively
        if (normalized.documents.worksheet1) {
            // Ensure worksheet1 is a copy, not a reference
            normalized.documents.worksheet1 = { ...normalized.documents.worksheet1 };
            const worksheet1 = normalized.documents.worksheet1;

            // Helper function to normalize taxInfoUsed, filingStatus, and taxStatus in any object
            const normalizeTaxInfoUsed = (obj: any): void => {
                if (!obj || typeof obj !== 'object') return;

                // Normalize taxInfoUsed.taxStatus
                if (obj.taxInfoUsed && obj.taxInfoUsed.taxStatus) {
                    obj.taxInfoUsed = {
                        ...obj.taxInfoUsed,
                        taxStatus: normalizeFilingStatus(obj.taxInfoUsed.taxStatus) ?? obj.taxInfoUsed.taxStatus,
                    };
                }

                // Normalize filingStatus field if it exists
                if (obj.filingStatus) {
                    obj.filingStatus = normalizeFilingStatus(obj.filingStatus) ?? obj.filingStatus;
                }

                // Normalize taxStatus field if it exists
                if (obj.taxStatus) {
                    obj.taxStatus = normalizeFilingStatus(obj.taxStatus) ?? obj.taxStatus;
                }

                // Recursively check nested objects and arrays
                for (const key in obj) {
                    if (obj[key] && typeof obj[key] === 'object') {
                        if (Array.isArray(obj[key])) {
                            // Handle arrays
                            obj[key].forEach((item: any) => {
                                if (item && typeof item === 'object') {
                                    normalizeTaxInfoUsed(item);
                                }
                            });
                        } else {
                            // Handle objects
                            normalizeTaxInfoUsed(obj[key]);
                        }
                    }
                }
            };

            // Normalize in petitioner and respondent sections
            if (worksheet1.petitioner) {
                // Ensure petitioner is a copy, not a reference
                worksheet1.petitioner = { ...worksheet1.petitioner };
                normalizeTaxInfoUsed(worksheet1.petitioner);

                // Calculate and populate applicableRateText
                const petitionerParty = getPetitionerParty(normalized);
                const petitionerApplicableRateText = calculateApplicableRateText(petitionerParty);
                debugLog(`Setting petitioner applicableRateText: ${petitionerApplicableRateText}`, {
                    petitionerParty: petitionerParty,
                    wages: petitionerParty?.income?.primary?.yearlyWages,
                    selfEmployment: petitionerParty?.income?.primary?.yearlySelfEmployment,
                    sectionExists: !!worksheet1.petitioner.socialSecurityPensionDeductionSection
                });

                // Ensure the section exists and set the value
                if (!worksheet1.petitioner.socialSecurityPensionDeductionSection) {
                    debugLog("petitioner.socialSecurityPensionDeductionSection is missing, creating it!");
                    worksheet1.petitioner.socialSecurityPensionDeductionSection = {
                        annualEarnedIncome: 0,
                        applicableRate: 0,
                        applicableRateText: petitionerApplicableRateText,
                        annualSocialSecurityAndMedicareTaxLiability: 0,
                        debug: {}
                    };
                } else {
                    // Ensure section is a copy, not a reference
                    worksheet1.petitioner.socialSecurityPensionDeductionSection = {
                        ...worksheet1.petitioner.socialSecurityPensionDeductionSection,
                        applicableRateText: petitionerApplicableRateText
                    };
                }
            }
            if (worksheet1.respondent) {
                // Ensure respondent is a copy, not a reference
                worksheet1.respondent = { ...worksheet1.respondent };
                normalizeTaxInfoUsed(worksheet1.respondent);

                // Calculate and populate applicableRateText
                const respondentParty = getRespondentParty(normalized);
                const respondentApplicableRateText = calculateApplicableRateText(respondentParty);
                debugLog(`Setting respondent applicableRateText: ${respondentApplicableRateText}`, {
                    respondentParty: respondentParty,
                    wages: respondentParty?.income?.primary?.yearlyWages,
                    selfEmployment: respondentParty?.income?.primary?.yearlySelfEmployment,
                    sectionExists: !!worksheet1.respondent.socialSecurityPensionDeductionSection
                });

                // Ensure the section exists and set the value
                if (!worksheet1.respondent.socialSecurityPensionDeductionSection) {
                    debugLog("respondent.socialSecurityPensionDeductionSection is missing, creating it!");
                    worksheet1.respondent.socialSecurityPensionDeductionSection = {
                        annualEarnedIncome: 0,
                        applicableRate: 0,
                        applicableRateText: respondentApplicableRateText,
                        annualSocialSecurityAndMedicareTaxLiability: 0,
                        debug: {}
                    };
                } else {
                    // Ensure section is a copy, not a reference
                    worksheet1.respondent.socialSecurityPensionDeductionSection = {
                        ...worksheet1.respondent.socialSecurityPensionDeductionSection,
                        applicableRateText: respondentApplicableRateText
                    };
                }
            }
        }

        // Normalize stepDown cases (recursive)
        if (normalized.documents.stepDownCases) {
            normalized.documents = {
                ...normalized.documents,
                stepDownCases: normalized.documents.stepDownCases.map(stepDownCase =>
                    normalizeCaseData(stepDownCase, counties)
                ),
            };
        }

        // Normalize alternatingEvenCase
        if (normalized.documents.alternatingEvenCase) {
            normalized.documents = {
                ...normalized.documents,
                alternatingEvenCase: normalizeCaseData(normalized.documents.alternatingEvenCase, counties),
            };
        }

        // Normalize splitCases
        if (normalized.documents.splitCases) {
            normalized.documents = {
                ...normalized.documents,
                splitCases: normalized.documents.splitCases.map(splitCase =>
                    normalizeCaseData(splitCase, counties)
                ),
            };
        }
    }

    // Normalize team property - convert empty string IDs to undefined
    if (normalized.team) {
        normalized.team = {
            ...normalized.team,
            // Convert empty string to undefined for team.id
            id: normalized.team.id === '' ? undefined : normalized.team.id,
        };
    }

    debugLog("Normalized case data!");

    return normalized;
}

/**
 * Future normalization functions can be added here as needed.
 * 
 * HOW TO ADD NEW NORMALIZATION RULES:
 * 
 * 1. Add a mapping constant (like FILING_STATUS_MAP) for the old -> new value mappings
 * 2. Create a normalization function (like normalizeFilingStatus) for that field
 * 3. Update normalizeCaseData() to call your new normalization function
 * 4. The normalization will automatically be applied when:
 *    - Loading cases from the API (getCase)
 *    - Saving cases and receiving the response (saveCase)
 * 
 * Examples of future normalizations:
 * - normalizeMedicalCoverageType() - convert 'hawk_i' to 'hawkI'
 * - normalizeCustodyArrangement() - convert 'party_a' to 'partyA'
 * - normalizeExpenseTypes() - convert old expense type codes to new ones
 * 
 * IMPORTANT: Normalization only happens when LOADING data from the API.
 * When saving, we send the data as-is (already in the new format).
 * This ensures backward compatibility with old data while using new formats going forward.
 */

